import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { api, type TenantInfo } from "../api.ts";
import { applyTheme } from "./theme.ts";

interface TenantState {
  info: TenantInfo | null;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantState | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const [info, setInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get<TenantInfo>(`/api/t/${slug}/`)
      .then((data) => {
        setInfo(data);
        applyTheme(data.tenant.theme);
        document.title = `${data.tenant.name} — EcommerceDashboard`;
      })
      .catch((err) => {
        setError(err.message ?? "tenant_load_error");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <TenantContext.Provider value={{ info, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be inside TenantProvider");
  return ctx;
}
